/**
 * Per-distributor sub-region card per scope §6.1.1. Atlas Day 2 Phase 3 +
 * Atlas Day 3a P1 cross-cell pointer wiring.
 *
 * Surface: "ISG works with N distributors; K active. Credit posture and
 * per-distributor overrides for params + cost lines."
 *
 * Cross-cell pointer: "→ Credit gate" wired per Atlas Day 3a P1 — clickable
 * button with stopPropagation; navigates to credit-gate inspect. Card body
 * navigates to per-distributor inspect. Refactored from <button> to <div
 * role="button"> to allow nested button for the cross-cell pointer
 * (avoids invalid nested-button HTML).
 */

import type { JSX, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import type { DistributorSubstrate } from '../../../lib/atlas/perEntitySubstrate';

export interface PerDistributorCardProps {
  substrate: DistributorSubstrate;
}

export default function PerDistributorCard({ substrate }: PerDistributorCardProps): JSX.Element {
  const navigate = useNavigate();
  const { total, active } = substrate;
  const goPrimary = (): void => {
    navigate('/admin/engine-atlas/inspect/per-distributor');
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goPrimary();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      data-sub-region-id="per-distributor"
      onClick={goPrimary}
      onKeyDown={onKey}
      className="w-full text-left border border-border-subtle bg-surface-0 rounded-md p-3 hover:border-accent transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <Building2 size={16} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-ink-bright">Per-distributor</h3>
            <button
              type="button"
              data-cross-cell-pointer="credit-gate"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/admin/engine-atlas/inspect/credit-gate');
              }}
              className="text-xs text-ink-muted hover:text-accent transition-colors cursor-pointer"
            >
              → Credit gate
            </button>
          </div>
          <p className="text-xs text-ink-secondary mt-1">
            ISG works with {total} {total === 1 ? 'distributor' : 'distributors'}; {active} active.
            Credit posture and per-distributor overrides for params + cost lines.
          </p>
        </div>
      </div>
    </div>
  );
}
